import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/Auth';
import API from '../../API';
import LogTableDisplay from '../../components/LogTableDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import styles from './TrackerPage.module.css';

export default function TrackerPage() {
    const { getQRListener, getLogsListener, updateLogToDeleted } = API;
    const { activeOrgId } = useAuth();
    const navigate = useNavigate();
    
    // ----- STATE INITIALIZATION -----
    const [ qrObj, setQrObj ] = useState(null);
    const [ editLogEntry, setEditLogEntry ] = useState(false);
    const [ deleteLogEntry, setDeleteLogEntry ] = useState(false);
    const [ selectedLog, setSelectedLog ] = useState(null);
    const [ deletedToggle, setDeletedToggle ] = useState(false);
    const [ logs, setLogs ] = useState([]);
    const [ showAddLog, setShowAddLog ] = useState(false);
    const authenticatedUser = useMemo(() => activeOrgId === qrObj?.org_id, [activeOrgId, qrObj?.org_id]);

    const { id } = useParams();
    // ----- END STATE INITIALIZATION -----
    // ----------------------------------------------

    useEffect(() => {
        let listener;
        let logsListener;

        if (id) {
            listener = getQRListener(id, setQrObj);
            logsListener = getLogsListener('tracker', id, setLogs);
        };

        return () => {
            listener && listener();
            logsListener && logsListener();
            setQrObj(null);
            setLogs([]);
        };
    }, []);

    useEffect(() => {
        if (!qrObj?.qr_type) return;

        if (!['tracker','workout-log','service-log','vehicle-log','maintenance-tracker','liquid-label','liquid-poster','garden-label'].includes(qrObj?.qr_type || '')) {
            if (['survey','review'].includes(qrObj?.qr_type)) {
                navigate(`/s/${id}`);
            } else {
                navigate('/notfound');
            };
        };
    }, [qrObj]);

    const handleDeleteLog = async () => {
        try {
            const res = await updateLogToDeleted(id, selectedLog.date, 'tracker');
            if (res === true) {
                setSelectedLog(null);
                setEditLogEntry(false);
                setDeleteLogEntry(false);
            };
        } catch (err) {
            console.log(err);
        };
    };

    const handleNavButtons = () => {
        if (!selectedLog && !showAddLog) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => navigate(`/collection`, { state: { qrID: id } }) } >Collection</button>
                </div>
            );
        };

        if (showAddLog) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setShowAddLog(false)} >Back</button>
                </div>
            );
        };

        if (deletedToggle) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setSelectedLog(null)} >Back</button>
                </div>
            );
        } else if (editLogEntry) {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setEditLogEntry(false)} >Back</button>
                </div>
            );
        } else if (deleteLogEntry) {
            return (
                <>
                    <div className={styles.navBtnCont}>
                        <button className={styles.cancelDeleteBtn} onClick={() => setDeleteLogEntry(false)} >Cancel</button>
                        <button className={styles.deleteBtn} onClick={handleDeleteLog} >Confirm</button>
                    </div>
                    <p className={styles.deleteText}>Deleting logs is permanent. Delete log?</p>
                </>
            );
        } else {
            return (
                <div className={styles.navBtnCont}>
                    <button className={styles.backBtn} onClick={() => setSelectedLog(null)}>Back</button>
                    <button className={styles.deleteBtn} onClick={() => setDeleteLogEntry(true)} >Delete</button>
                </div>
            );
        };
    };

    if (!qrObj || !qrObj?.tracker || !['tracker','workout-log','service-log','vehicle-log','maintenance-tracker','liquid-label','liquid-poster','garden-label'].includes(qrObj?.qr_type || '')) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.display} >
                    <div className={styles.navContainer}>
                        {(authenticatedUser) ?
                            handleNavButtons()
                        : selectedLog ?
                            <div className={styles.navBtnCont}>
                                <button className={styles.backBtn} onClick={() => setSelectedLog(null)} >Back</button>
                            </div>
                        :
                            <></>
                        }
                        {(authenticatedUser) ? ((selectedLog) ? <button className={styles.editBtn} onClick={() => setEditLogEntry(true)} disabled={selectedLog?.deleted} >Edit</button> : <button className={styles.editBtn} onClick={() => navigate(`/collection/${id}/edit?tab=configure`) }>Edit</button>) : <></>}
                    </div>
                    <div className={styles.content}>
                        <LogTableDisplay
                            id={id}
                            qrType={'tracker'}
                            qrObj={qrObj}
                            qrData={qrObj?.tracker}
                            logs={logs || []}
                            editLogEntry={editLogEntry}
                            setEditLogEntry={setEditLogEntry}
                            selectedLog={selectedLog}
                            setSelectedLog={setSelectedLog}
                            authenticatedUser={authenticatedUser}
                            deletedToggle={deletedToggle}
                            setDeletedToggle={setDeletedToggle}
                            onShowAddLogChange={setShowAddLog}
                            externalShowAddLog={showAddLog}
                            externalSetShowAddLog={setShowAddLog}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
